import React, { useEffect, useState } from 'react';
import { TiArrowBack } from "react-icons/ti";
import { FaQuestionCircle } from "react-icons/fa";
import { Tooltip } from 'react-tooltip';
import PortfolioComposite from '../../../api/models/portfolioComposite';
import DeviationGauge from '../deviation-gauge';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setSelectedComposite } from './portfolio-detail-slice';
import { selectPortfolios } from '../portfolios-slice';
import { instanceToPlain } from 'class-transformer';
import { componentIdealPercentageString } from '../../../app/utils';

interface PortfolioDetailCompositeSummaryProps {
  portfolioComposite: PortfolioComposite
}

/**
 * A summary of the portfolio.
 */
const PortfolioDetailCompositeSummary: React.FC<PortfolioDetailCompositeSummaryProps> = (props: PortfolioDetailCompositeSummaryProps) => {

  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const portfolios = useAppSelector(selectPortfolios);
  const isRootComposite = (!props.portfolioComposite.parentCompositeId);

  const [previousComposite, setPreviousComposite] = useState(instanceToPlain(props.portfolioComposite));

  // When the portfolio composite in the properties change, save the previous one.
  useEffect(() => {
    setPreviousComposite(previousComposite);
  }, [previousComposite, props.portfolioComposite])

  function goBackToPreviousCompositeHandler(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    if (props.portfolioComposite.parentCompositeId) {
      const compositeSelectedPrevious = portfolios[0].portfolioComposite?.getComponentById(props.portfolioComposite.parentCompositeId)
      dispatch(setSelectedComposite(instanceToPlain(compositeSelectedPrevious)));
    }
  }

  return (
    <div className='flex h-full w-full items-center justify-center px-9 pt-9 lg:p-9 select-none font-semibold text-xs lg:text-base'>
      <div className='flex flex-col h-full w-full rounded-lg items-center justify-center overflow-hidden bg-black/10 border lg:border-2 border-blue-300/40 shadow-2xl shadow-slate-900/80'>

        <div className='flex w-full pl-5 py-1 text-center text-sm lg:text-lg items-center border-b lg:border-b-2 border-blue-300/40 bg-blue-900/20'>
          <div className='grow text-start'>{props.portfolioComposite.category.name.toUpperCase()}</div>
          <div className='h-2/3 mx-3 border-l lg:border-l-2 border-blue-300/40'></div>
          <div className='flex' data-tooltip-id='tooltipIdealPercentage'>
            <div className='text-end' >{componentIdealPercentageString(props.portfolioComposite)}</div>
            <div className='flex items-center justify-center mx-2'><FaQuestionCircle size={15} /></div>
          </div>
          <Tooltip place='bottom' id='tooltipIdealPercentage' >
            <div className='flex flex-col items-center justify-center text-xs font-semibold text-neutral-300'>
              <label>% ideal do portfolio</label>
            </div>
          </Tooltip>
        </div>

        <div className='relative flex flex-col h-full w-full'>

          {
            // If there is a parent composite, show the return arrow button.
            props.portfolioComposite.parentCompositeId &&
            <div onClick={goBackToPreviousCompositeHandler} className='cursor-pointer absolute left-5 top-5 flex items-center justify-center w-9 lg:w-12 aspect-square rounded-full border border-white/40 bg-white/20'>
              <TiArrowBack size={30} className='text-white' />
            </div>
          }

          <div className='flex flex-col min-w-fit min-h-fit items-center justify-center lg:mt-10'>
            <DeviationGauge
              deviation={props.portfolioComposite.weightedAvgAbsDeviation()}
              deviationLimit={0.3}
              className='flex w-2/3 lg:w-48 mt-3 items-center justify-center'
              valueClassName='text-center w-full mt-2 text-sm lg:text-lg bg-transparent'
            />
          </div>

          <div className='flex flex-row w-full h-full lg:text-base'>

            <div className='flex flex-row w-full h-full justify-start pl-4 pb-4'>
              {
                !isRootComposite &&
                <div className='w-fit text-start'>
                  <div className='border-b'>Valor IDEAL</div>
                  <label className='font-light'>R$ {props.portfolioComposite.idealNotional().toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
                  <div className='mt-2 border-b'>% IDEAL</div>
                  <label className='font-light'>{(props.portfolioComposite.idealPercentage() * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</label>
                </div>
              }
            </div>

            <div
              data-tooltip-id='tooltipDeviation'
              className='min-w-fit h-fit flex flex-col items-center justify-center'>
              <label className='lg:text-lg'>
                Desvio
              </label>
              <div className='flex flex-row items-center justify-center mb-2'>
                <label className='font-extralight lg:text-base mr-1'>
                  (Médio)
                </label>
                <FaQuestionCircle size={15} />
              </div>
            </div>
            <Tooltip place='top' id='tooltipDeviation' >
              <div className='flex flex-col items-center justify-center text-xs font-semibold text-neutral-300'>
                <label>Média dos desvios absolutos</label>
                <label>das subcategorias e posições,</label>
                <label>pertencentes à categoria selecionada,</label>
                <label>ponderados por seus valores.</label>
              </div>
            </Tooltip>

            <div className='flex flex-row w-full h-full justify-end pr-4 pb-4'>
              <div className='w-fit text-end'>
                <div className='border-b'>Valor ATUAL</div>
                <label className='font-light'>R$ {props.portfolioComposite.notional().toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
                {
                  !isRootComposite &&
                  <React.Fragment>
                    <div className='mt-2 border-b'>% ATUAL</div>
                    <label className='font-light'>{(props.portfolioComposite.currentPercentage() * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</label>
                  </React.Fragment>
                }
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default PortfolioDetailCompositeSummary;